import { color, icon, stroke, testColors, type TestTone } from "@mesketch/design-tokens";
import type { ReactElement } from "react";

// Kresby předmětů typů (DES-003 §6, typ-hexaco-po-kolech.html): tužka → inkoust → barva. Ve
// fázi 0 kompas a maják podle mockupu, ostatní předměty jsou zástupné tvary a mozaika
// (zadání 23). Cesty mají `pathLength="1"` a třídy `mk-stroke` a `mk-fill`, kreslení tahem
// a naskočení výplně řídí CSS webu. Komponenta je bez vedlejších efektů (Satori, PDF).

export type DrawingId = "compass" | "lighthouse" | "mosaic" | `placeholder-${number}`;

/** Fáze kresby: dvě skici přes sebe, tužka, inkoust, barva (DES-003 §6, zadání 7.4). */
export type DrawingStage = "ghost" | "pencil" | "ink" | "color";

interface Palette {
  readonly main: string;
  readonly bg: string;
  readonly ink: string;
  readonly light: string;
}

type Shape = (p: Palette) => ReactElement;

const S = { className: "mk-stroke", pathLength: 1 } as const;
const F = (fill: string) => ({ className: "mk-stroke mk-fill", pathLength: 1, fill });

const compass: Shape = (p) => (
  <>
    <circle {...F(p.main)} cx="110" cy="94" r="50" />
    <circle {...F(color.card)} cx="110" cy="94" r="41" />
    <circle {...S} cx="110" cy="38" r="6" fill="none" />
    <path {...S} fill="none" d="M75 94 H81 M139 94 H145" />
    <polygon {...F(p.ink)} points="110,59 117,94 103,94" />
    <polygon {...F(p.light)} points="103,94 117,94 110,129" />
    <circle className="mk-fill" cx="110" cy="94" r="3" fill={color.paper} />
  </>
);

const lighthouse: Shape = (p) => (
  <g transform="translate(48 0)">
    <polygon {...F(color.card)} points="48,152 76,152 71,70 53,70" />
    <rect {...F(icon.strengthInner)} x="51" y="56" width="22" height="14" />
    <polygon {...F(p.main)} points="49,56 62,44 75,56" />
    <path {...S} fill="none" d="M51.3 98 H72.7 M49.7 124 H74.3" />
    <path {...S} fill="none" d="M74 60 L96 52 M74 66 L96 74 M50 60 L28 52 M50 66 L28 74" />
  </g>
);

const mosaic: Shape = (p) => (
  <>
    {[
      [80, 62, p.main],
      [112, 62, p.light],
      [80, 94, p.light],
      [112, 94, p.ink],
    ].map(([x, y, fill]) => (
      <rect key={`${x}-${y}`} {...F(String(fill))} x={Number(x)} y={Number(y)} width="28" height="28" rx="4" />
    ))}
  </>
);

/** Zástupný tvar: pravidelný mnohoúhelník s kruhem, počet stran podle čísla tvaru. */
function placeholder(sides: number): Shape {
  const pts = Array.from({ length: sides }, (_, i) => {
    const a = (Math.PI * 2 * i) / sides - Math.PI / 2;
    return `${(110 + 46 * Math.cos(a)).toFixed(1)},${(98 + 46 * Math.sin(a)).toFixed(1)}`;
  }).join(" ");
  return (p) => (
    <>
      <polygon {...F(p.bg)} points={pts} />
      <circle {...F(p.main)} cx="110" cy="98" r="15" />
    </>
  );
}

function shapeOf(id: DrawingId): Shape {
  if (id === "compass") return compass;
  if (id === "lighthouse") return lighthouse;
  if (id === "mosaic") return mosaic;
  return placeholder(3 + (Number(id.slice("placeholder-".length)) % 5));
}

const DETAIL: Shape = () => (
  <>
    <path {...S} fill="none" d="M34 33 v10 M29 38 h10 M56 18 v8 M52 22 h8 M146 16 v8 M142 20 h8 M199 62 v8 M195 66 h8" />
    <path {...F(icon.strengthInner)} d="M178 26 A13 13 0 1 0 178 50 A15 15 0 0 1 178 26 Z" />
  </>
);

function lineStyle(stage: DrawingStage | "pencil-detail") {
  const pencil = stage === "ghost" || stage === "pencil" || stage === "pencil-detail";
  return {
    stroke: pencil ? color.pencilLine : color.ink,
    strokeWidth: pencil ? stroke.pencil : stroke.ink,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  } as const;
}

/** Druhý posunutý tah tužky (DES-003 §6). */
function pencilEcho(shape: ReactElement) {
  return (
    <g transform="translate(1.4 -1.2)" stroke={color.pencilLine} strokeWidth={stroke.pencilSecond} opacity={stroke.pencilSecondOpacity} fill="none" className="mk-echo">
      {shape}
    </g>
  );
}

export interface TypeDrawingProps {
  readonly primary: DrawingId;
  /** Druhá nejpravděpodobnější skica, jen ve fázi `ghost`. */
  readonly secondary?: DrawingId | null;
  readonly stage: DrawingStage;
  /** Detail od kola 3: tužkou nebo inkoustem podle stavu přídavného jména. */
  readonly detail?: "pencil" | "ink" | null;
  readonly tone?: TestTone;
  readonly label: string;
}

export function TypeDrawing({ primary, secondary = null, stage, detail = null, tone = "who", label }: TypeDrawingProps) {
  const tc = testColors[tone];
  const palette: Palette = { main: tc.main, bg: tc.bg, ink: tc.ink, light: tone === "who" ? icon.whoFacets[0] : tc.bg };
  const colored = stage === "color";
  const main = shapeOf(primary)(palette);
  return (
    <svg viewBox="0 0 220 170" width="100%" role="img" aria-label={label} data-stage={stage} className={`mk-drawing mk-${stage}`} style={{ display: "block" }}>
      <rect x="6" y="6" width="208" height="146" rx="10" fill={tc.bg} className="mk-fill" opacity={colored ? 1 : 0} />
      <g {...lineStyle(stage)} fill="none">
        <line {...S} x1="30" y1="152" x2="190" y2="152" />
      </g>
      {stage === "ghost" && secondary !== null ? (
        <g opacity="0.6" data-part="secondary" {...lineStyle("ghost")}>
          <g fillOpacity="0">{shapeOf(secondary)(palette)}</g>
        </g>
      ) : null}
      {stage === "ghost" || stage === "pencil" ? pencilEcho(<g fillOpacity="0">{main}</g>) : null}
      <g data-part="primary" opacity={stage === "ghost" ? 0.6 : 1} {...lineStyle(stage)}>
        <g fillOpacity={colored ? 1 : 0}>{main}</g>
      </g>
      {detail !== null ? (
        <g data-part="detail" {...lineStyle(detail === "pencil" ? "pencil-detail" : "ink")}>
          <g fillOpacity={colored ? 1 : 0}>{DETAIL(palette)}</g>
        </g>
      ) : null}
    </svg>
  );
}
